import {
  useCompanyInvitesQuery,
  useCompanyMembersQuery,
  useRespondToPermissionUpgradeRequestMutation,
} from "@/api/companyApi";
import UserGuideIcon from "@/assets/dark_gradient/user_guide.svg";
import Loader from "@/components/Common/kpi/Loader";
import Modal from "@/components/Common/Modal";
import CompanyMembers from "@/components/MultiAccount/CompanyMembers";
import PermissionGuide from "@/components/MultiAccount/PermissionGuide";
import {useIsAdmin, useSelectedCompany} from "@/hooks/useUserHooks";
import ProfileSeparator from "@/pages/profile/ProfileSeparator";
import {useCompanyStore} from "@/store/company";
import {multiAccountInviteModalAtom} from "@/store/jotaiAtoms";
import {useAtom} from "jotai";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";

const InviteModal: FC = () => {
  const {t} = useTranslation(["multi_account", "common"]);
  const [inviteModal, setInviteModal] = useAtom(multiAccountInviteModalAtom);
  const isAdmin = useIsAdmin();
  const companyFromStore = useCompanyStore(state => state.company);
  const selectedCompany = useSelectedCompany();
  const company = isAdmin && !inviteModal.isViewingOwnCompany ? companyFromStore : selectedCompany;
  const [showPermissionGuide, setShowPermissionGuide] = useState(false);

  const {data: companyMembers, isLoading: isLoadingCompanyMembers} = useCompanyMembersQuery({
    companyId: company?.id!,
    queryOptions: {
      refetchOnMount: true,
    },
  });
  const {data: companyInvites, isLoading: isLoadingCompanyInvites} = useCompanyInvitesQuery({
    companyId: company?.id!,
    queryOptions: {
      refetchOnMount: true,
    },
  });

  const {mutate: upgradePermission} = useRespondToPermissionUpgradeRequestMutation({companyId: company?.id!});

  return (
    <Modal
      onClose={() =>
        setInviteModal({
          isVisible: false,
          isViewingOwnCompany: false,
        })
      }
      modalTitle={t("invite_modal_title")}
      className="min-h-120"
      backgroundClassName="flex max-w-150"
      customBottomRow={<></>}>
      <div className="flex flex-col gap-5">
        {isLoadingCompanyMembers || isLoadingCompanyInvites ? (
          <Loader />
        ) : (
          <CompanyMembers
            companyId={company?.id!}
            companyName={company?.company_name ?? ""}
            companyMembers={companyMembers ?? []}
            companyInvites={companyInvites?.filter(invite => invite.status === "PENDING") ?? []}
            upgradePermission={memberId => upgradePermission({user_id: memberId, status: "REJECTED"})}
          />
        )}
        <ProfileSeparator
          text={t("multi_account:permission_guide")}
          icon={UserGuideIcon}
          isToggleable
          isToggled={showPermissionGuide}
          onToggle={() => setShowPermissionGuide(!showPermissionGuide)}
        />
        {showPermissionGuide && <PermissionGuide />}
        <div className="h-10" />
      </div>
    </Modal>
  );
};

export default InviteModal;
